'use client';

import { type HTMLAttributes, memo, type ReactNode, type Ref, useMemo } from 'react';
import { autoUpdate, flip, type Middleware, offset, type Placement, size, type UseFloatingOptions, useFloating } from '@floating-ui/react-dom';
import { clsx } from 'clsx';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { useMergeRefs } from '@interxfi/utilities/ref/merge-refs';
import { useIntersectionObserverAction } from '../../../../util/intersection-observer';
import { HorizontalStack } from '../../../alignment/horizontal-stack';
import { Skeleton } from '../../../loading/skeleton';
import { Typography } from '../../../typography';
import styles from '../../../dropdown/dropdown.module.scss';
import inputStyles from '../../input.module.scss';

export interface BaseInputDropdownOption {
  aliases?: string[];
  className?: string;
  group?: string;
  label: string;
  description?: ReactNode;
  onVisible?: VoidFunction;
  pillLabel?: string;
  prefix?: ReactNode;
  showSkeletonCharacters?: number;
  suffix?: ReactNode;
}

export interface InputDropdownOption<T extends string | undefined = string> extends BaseInputDropdownOption {
  value: T;
}

export interface InputDropdownOptionMultiple<T extends string | undefined = string> extends BaseInputDropdownOption {
  id: string;
  value: T[];
}

const FLOATING_DROPDOWN_PADDING = 8;
const FLOATING_DROPDOWN_ITEM_HEIGHT = 32;

export interface InputDropdownPagination<T = any> {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<T>;
}

function getFloatingDropdownMiddleware(itemCount: number, matchWidth?: boolean): Middleware[] {
  return [
    offset(FLOATING_DROPDOWN_PADDING),
    size({
      padding: FLOATING_DROPDOWN_PADDING,
      apply: ({ availableHeight, elements, rects }) => {
        Object.assign(elements.floating.style, {
          // Minimum acceptable height is 3 items
          maxHeight: `${Math.max(Math.min(itemCount, 3) * FLOATING_DROPDOWN_ITEM_HEIGHT, availableHeight)}px`,
          // Force floating element to match reference width, if configured
          width:
            matchWidth || getComputedStyle(elements.floating).maxWidth === '100%'
              ? `${rects.reference.width}px`
              : rects.floating.width
                ? `${rects.floating.width}px`
                : 'auto',
        });
      },
    }),
    flip({ fallbackStrategy: 'initialPlacement' }),
  ];
}

export function inputDropdownOptionToString(option: BaseInputDropdownOption | null) {
  return option?.label ?? '';
}

export function getOptionsByGroup<T extends { group?: string }>(options: T[] | undefined) {
  return (options || []).reduce(
    (grouped, option) => {
      const group = option.group || '';
      grouped[group] = grouped[group] || [];
      grouped[group].push(option);

      return grouped;
    },
    {} as Record<string, T[]>,
  );
}

export function useFloatingDropdown(
  itemCount: number,
  isOpen: boolean,
  matchWidth: boolean = true,
  placement: Placement = 'bottom',
  elements?: UseFloatingOptions['elements'],
) {
  const floatingMiddleware = useMemo(() => getFloatingDropdownMiddleware(itemCount, matchWidth), [itemCount, matchWidth]);
  const floatingOptions: UseFloatingOptions = useMemo(
    () => ({
      placement,
      strategy: 'fixed',
      whileElementsMounted: isOpen ? autoUpdate : undefined,
      open: isOpen,
      middleware: floatingMiddleware,
      elements,
    }),
    [floatingMiddleware, isOpen, placement, elements],
  );

  return useFloating(floatingOptions);
}

export type GetInputDropdownItemProps<T extends string = string> = (
  options: HTMLAttributes<HTMLLIElement> & {
    item: InputDropdownOption<T> | InputDropdownOptionMultiple<T>;
    index: number;
    ref: Ref<HTMLLIElement> | undefined;
  },
) => HTMLAttributes<HTMLLIElement>;

function determineIsActive<T extends string>(
  item: InputDropdownOption<T> | InputDropdownOptionMultiple<T>,
  selectedItemValue: T | T[] | (T | T[])[] | undefined,
) {
  if (Array.isArray(item.value)) {
    return Array.isArray(selectedItemValue) ? item.value.every((val) => selectedItemValue.includes(val)) : false;
  }

  return Array.isArray(selectedItemValue) ? selectedItemValue.includes(item.value) : selectedItemValue === item.value;
}

export interface InputDropdownItemProps<T extends string = string> extends HTMLAttributes<HTMLLIElement> {
  getItemProps: GetInputDropdownItemProps<T>;
  highlightedIndex: number;
  index: number;
  item: InputDropdownOption<T> | InputDropdownOptionMultiple<T>;
  ref?: Ref<HTMLLIElement>;
  selectedItemValue: T | T[] | (T | T[])[] | undefined;
}

const InputDropdownItem = memo(
  <T extends string = string>({
    className,
    getItemProps,
    highlightedIndex,
    index,
    item,
    selectedItemValue,
    ref,
    ...rest
  }: InputDropdownItemProps<T>) => {
    const [setObservedItemRef] = useIntersectionObserverAction(item.onVisible, {
      disabled: !item.onVisible,
    });
    const mergedRefs = useMergeRefs(ref, setObservedItemRef);
    // Skeleton rows never show a suffix
    const showSuffix = !item.showSkeletonCharacters && Boolean(item.suffix);
    const content = (
      <VerticalStack className={showSuffix ? styles.itemContent : undefined}>
        <HorizontalStack blockAlign="center" className={styles.itemText} gap={2}>
          {!item.showSkeletonCharacters && item.prefix ? <span className={styles.prefix}>{item.prefix}</span> : null}
          <span className={clsx(styles.option, item.className)}>
            {item.showSkeletonCharacters ? <Skeleton charCount={item.showSkeletonCharacters} type="line" /> : item.label}
          </span>
        </HorizontalStack>

        {item.description ? (
          <Typography lineHeight="1rem" size="0.75rem" tone="neutral-dk">
            {item.description}
          </Typography>
        ) : null}
      </VerticalStack>
    );

    return (
      <li
        {...getItemProps({
          item,
          index,
          ...rest,
          className: clsx(
            styles.item,
            styles.paddedItem,
            highlightedIndex === index && styles.isHighlighted,
            determineIsActive<T>(item, selectedItemValue) && styles.isSelected,
            className,
          ),
          ref: mergedRefs,
        })}
      >
        {/* Only wrap in a row when there is a suffix, so options without one keep their layout. */}
        {showSuffix ? (
          <HorizontalStack blockAlign="center" className={styles.itemRow} gap={2} wrap="nowrap">
            {content}
            <span className={styles.suffix}>{item.suffix}</span>
          </HorizontalStack>
        ) : (
          content
        )}
      </li>
    );
  },
);

export type InputDropdownGroupStyle = 'heading' | 'border';

interface InputDropdownProps<T extends string = string> extends HTMLAttributes<HTMLDivElement> {
  getItemProps: GetInputDropdownItemProps<T>;
  groupStyle?: InputDropdownGroupStyle;
  highlightedIndex: number;
  inputId: string;
  isLoading?: boolean;
  noOptionsMessage?: ReactNode;
  options: (InputDropdownOption<T> | InputDropdownOptionMultiple<T>)[];
  pagination?: InputDropdownPagination;
  ref?: Ref<HTMLDivElement>;
  selectedItemValue: T | T[] | (T | T[])[] | undefined;
}

export function InputDropdown<T extends string = string>({
  className,
  getItemProps,
  groupStyle = 'heading',
  highlightedIndex,
  inputId,
  isLoading,
  noOptionsMessage = 'No options',
  options,
  pagination,
  ref,
  selectedItemValue,
  ...rest
}: InputDropdownProps<T>) {
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pagination || {};
  const [setObservedItemRef] = useIntersectionObserverAction(fetchNextPage, {
    disabled: !hasNextPage || isFetchingNextPage || !fetchNextPage,
  });
  const optionsByGroup = useMemo(() => getOptionsByGroup(options), [options]);
  const hasMultipleGroups = Object.keys(getOptionsByGroup(options)).length > 1;
  const listboxProps = { ...rest, className: clsx(inputStyles.input, styles.dropdown, className), ref };
  const loadingNextDataIndicator =
    isLoading || isFetchingNextPage ? (
      <>
        <li className={styles.pendingPagination} role="presentation">
          <Skeleton charCount={20} />
        </li>
        <li className={styles.pendingPagination} role="presentation">
          <Skeleton charCount={30} />
        </li>
        <li className={styles.pendingPagination} role="presentation">
          <Skeleton charCount={15} />
        </li>
      </>
    ) : null;
  const optionsToDisplay = options || STABLE_EMPTY_ARRAY_REF;

  if (!options.length) {
    return (
      <div {...(listboxProps as HTMLAttributes<HTMLDivElement>)}>
        <ul>
          {loadingNextDataIndicator || (
            <li className={styles.noMatchingOptions} role="presentation">
              <Typography className={styles.noOptionsMessage}>{noOptionsMessage}</Typography>
            </li>
          )}
        </ul>
      </div>
    );
  }

  return hasMultipleGroups ? (
    <div {...(listboxProps as HTMLAttributes<HTMLDivElement>)}>
      {Object.entries(optionsByGroup).map(([group, groupOptions], i, arr) => {
        const groupLabelId = `${inputId}-group-${group.replace(/\s+/g, '-').toLowerCase()}`;

        return (
          <ul
            aria-label={groupStyle === 'heading' ? undefined : group}
            aria-labelledby={groupStyle === 'heading' ? groupLabelId : undefined}
            className={clsx(styles.group, groupStyle === 'border' && i !== arr.length - 1 && styles.withBorder)}
            key={group}
          >
            {groupStyle === 'heading' && (
              <li className={styles.heading} id={groupLabelId} role="presentation">
                <Typography transform="uppercase" weight="bold">
                  {group}
                </Typography>
              </li>
            )}

            {groupOptions.map((option) => {
              const optionIndex = optionsToDisplay.findIndex((optionFromList) => optionFromList.value === option.value);

              return (
                <InputDropdownItem
                  getItemProps={getItemProps as GetInputDropdownItemProps}
                  highlightedIndex={highlightedIndex}
                  index={optionIndex}
                  item={option}
                  key={Array.isArray(option.value) ? option.value.join('-') : option.value}
                  ref={optionIndex === optionsToDisplay.length - 1 ? setObservedItemRef : undefined}
                  selectedItemValue={selectedItemValue}
                />
              );
            })}
          </ul>
        );
      })}
      {loadingNextDataIndicator ? <ul>{loadingNextDataIndicator}</ul> : null}
    </div>
  ) : (
    <ul {...(listboxProps as HTMLAttributes<HTMLUListElement>)}>
      {optionsToDisplay.map((option) => {
        const optionIndex = optionsToDisplay.findIndex((optionFromList) => optionFromList.value === option.value);

        return (
          <InputDropdownItem
            getItemProps={getItemProps as GetInputDropdownItemProps}
            highlightedIndex={highlightedIndex}
            index={optionIndex}
            item={option}
            key={Array.isArray(option.value) ? option.value.join('-') : option.value}
            ref={optionIndex === optionsToDisplay.length - 1 ? setObservedItemRef : undefined}
            selectedItemValue={selectedItemValue}
          />
        );
      })}

      {loadingNextDataIndicator}
    </ul>
  );
}
